import * as React from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import { getMainLegs, getBookedVehicle, getOperatedVehicle } from '@src/shared/src/util/trips';
import { formatDate } from '@src/shared/src/util/date';
import { getVehicleIcon } from '@toolkit/util/app';
import { findBySelected } from '@src/shared/src/util/general';
import { getTariffFareName } from '@src/shared/src/util/tariffs';
// Constants
import { DATE_FORMAT_TYPES, VEHICLE_TYPES } from '@src/shared/src/const/app';

// Actions
// Models
import {
  LegModel,
  TripModel,
  TransportSegmentModel,
  BasketItemModel,
} from '@src/shared/src/models';
// Interfaces
// Components
import { BookingInfoItem } from '@src/components/BookingInfoItem';
import { GroundTransportBadge } from '@src/components/GroundTransportBadge';
// Styles
import '../styles/ConfirmedBookingTransportSegment.scss';

type Props = {
  trip: TripModel;
  basketItems: BasketItemModel[];
};

const ConfirmedBookingTransportSegment: React.FC<Props> = (props) => {
  const { trip, basketItems } = props;
  const { t } = useTranslation();

  const tripLegs = getMainLegs(trip.legs);

  const getFareName = (leg: LegModel) => {
    const tariff = findBySelected(leg.legOption.tariffs);
    return getTariffFareName(basketItems, tariff.id);
  };

  const tripInfo = (leg: LegModel, transportSegment: TransportSegmentModel) => {
    const product =
      transportSegment.vehicles[0] === VEHICLE_TYPES.PLANE
        ? `${transportSegment.operators[0]} ${transportSegment.products[0]}`
        : transportSegment.products.join(', ');

    const fareType = transportSegment.fareName ?? getFareName(leg);
    // One text node, as before: the products row is a flex container, so splitting
    // this up would replace the spaces around the separator with its 8px gap.
    return (
      <span>
        {product}
        <GroundTransportBadge groundTransport={transportSegment.groundTransport} />
        {` | ${fareType}`}
      </span>
    );
  };

  return (
    <div className="confirmed-booking-transport-segments">
      {tripLegs.map((leg: LegModel) => {
        return (
          <div key={leg.id}>
            {leg.legOption.transportSegments.map(
              (transportSegment: TransportSegmentModel, index: number) => (
                <div key={transportSegment.id}>
                  {/*
                    The two icons of a segment answer the two different questions it
                    raises: the header says what the leg is sold as, a flight, and the
                    products row below says what this segment is operated as, next to
                    the badge and the flight number that explain the pairing
                  */}
                  <BookingInfoItem
                    icon={
                      index === 0 ? (
                        <i className={`icon ${getVehicleIcon(getBookedVehicle(transportSegment))}`} />
                      ) : null
                    }
                    depName={transportSegment.depName}
                    depAt={
                      transportSegment.depPlatform &&
                      `${t('transportSegment.label.platform.dep')} ${transportSegment.depPlatform}`
                    }
                    depTime={formatDate(transportSegment.depAt, DATE_FORMAT_TYPES.LONG_TIME)}
                    arrName={transportSegment.arrName}
                    arrAt={
                      transportSegment.arrPlatform &&
                      `${t('transportSegment.label.platform.arr')} ${transportSegment.arrPlatform}`
                    }
                    arrTime={formatDate(transportSegment.arrAt, DATE_FORMAT_TYPES.LONG_TIME)}
                    changeover={index != leg.legOption.transportSegments.length - 1}
                    technicalStops={transportSegment.technicalStops}
                  />
                  <div className="confirmed-booking-transport-segments-products">
                    <i className={`icon ${getVehicleIcon(getOperatedVehicle(transportSegment))}`} />
                    {tripInfo(leg, transportSegment)}
                  </div>
                </div>
              ),
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConfirmedBookingTransportSegment;
